import { closeBillboardDetails } from "./Billboard";
import { t } from "../i18n";
import type { CityAdvertisement, CityAsset, CityObjectData } from "../api/types";
import { ObjectLayer } from "../objects/ObjectLayer";
import { IsoProjection } from "../core/projection";

export function safeAdvertisementUrl(value: string): string | null {
  try {
    const url = new URL(value);
    return ["https:", "http:"].includes(url.protocol) && !url.username && !url.password ? url.href : null;
  } catch { return null; }
}

export async function createCityAdvertisements(
  projection: IsoProjection, advertisements: CityAdvertisement[], host: HTMLElement,
): Promise<{ layer: ObjectLayer; destroy: () => void }> {
  const billboards = advertisements.filter((item) => item.slot === "city_billboard" && !item.objectId);
  const assets: CityAsset[] = billboards.map((item) => ({
    assetId: `advertisement-${item.id}`, name: item.title, sourceUrl: item.image,
    anchorX: 0.5, anchorY: 1, footprintWidth: 2, footprintDepth: 1, visualHeight: 2,
  }));
  const objects: CityObjectData[] = billboards.map((item) => ({
    id: `advertisement-${item.id}`, assetId: `advertisement-${item.id}`, name: item.title,
    layerId: "advertising", x: item.x, y: item.y, elevation: 0,
    scale: item.scale, scaleX: 1, scaleY: 1, rotation: item.rotation,
    layerPriority: 0, locked: true, visible: true,
  }));
  const layer = new ObjectLayer(projection, assets);
  layer.label = "city-advertisements";
  layer.zIndex = 90;
  await layer.load(objects);
  layer.onSelect((object) => {
    const advertisement = billboards.find((item) => object.id === `advertisement-${item.id}`);
    const url = advertisement && safeAdvertisementUrl(advertisement.destinationUrl);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  });
  const banners = advertisements.filter((item) => item.slot === "city_banner").slice(0, 3);
  const banner = document.createElement("aside");
  banner.className = "city-advertisement-banner";
  banner.setAttribute("aria-label", t("Sponsored", "تبلیغات"));
  for (const item of banners) {
    const url = safeAdvertisementUrl(item.destinationUrl);
    const card = document.createElement(url ? "a" : "div");
    card.className = "city-advertisement-banner__card";
    if (card instanceof HTMLAnchorElement && url) {
      card.href = url; card.target = "_blank"; card.rel = "sponsored noopener noreferrer";
    }
    const image = document.createElement("img");
    image.src = item.image; image.alt = item.title; image.loading = "lazy";
    const label = document.createElement("span");
    label.textContent = item.title;
    card.append(image, label);
    banner.appendChild(card);
  }
  if (banners.length) {
    const label = document.createElement("small");
    label.textContent = t("Sponsored", "تبلیغات");
    banner.prepend(label);
    host.appendChild(banner);
  }
  return { layer, destroy: () => { closeBillboardDetails(); banner.remove(); layer.destroy({children: true}); } };
}
